import { inject, Injectable } from "@angular/core";
import type { Project } from "@domain/project/base-project";
import { FloorplanItemReferenceMigrationService } from "@domain/project/floorplan-item-reference-migration.service";
import { ProjectFloorplanMigrationService } from "@domain/project/project-floorplan-migration.service";
import { ProjectFloorplanService } from "@domain/project/project-floorplan.service";
import type { ScopedProject } from "@domain/project/project-guards";
import { ProjectImageMigrationService } from "@domain/project/project-image-migration.service";
import { catchError, defaultIfEmpty, map, Observable, of, switchMap } from "rxjs";

@Injectable({
  providedIn: "root",
})
export class ProjectMigrationService {
  private projectImageMigrationService = inject(ProjectImageMigrationService);
  private projectFloorplanMigrationService = inject(ProjectFloorplanMigrationService);
  private projectFloorplanItemReferenceMigrationService = inject(FloorplanItemReferenceMigrationService);
  private projectFloorplanService = inject(ProjectFloorplanService);

  runMigrations(project: ScopedProject, persist$: (p: ScopedProject) => Observable<unknown>): Observable<ScopedProject> {
    const migrateImages$ =
      (project as any).images?.length >= 1
        ? this.projectImageMigrationService.migrateOldProjectImages(project).pipe(catchError(() => of(false)))
        : of(false);

    const migrateFloorplans$ =
      (project as any).floorplans?.length >= 1
        ? this.projectFloorplanMigrationService.migrateOldProjectFloorplans(project).pipe(catchError(() => of(false)))
        : of(false);

    return migrateImages$.pipe(
      switchMap((imagesMigrated) =>
        migrateFloorplans$.pipe(
          switchMap((floorplansMigrated) =>
            this.projectFloorplanMigrationService.migrateExtractedFloorplanGroups(project).pipe(
              catchError(() => of(false)),
              map((groupsMigrated) => imagesMigrated || floorplansMigrated || groupsMigrated),
            ),
          ),
        ),
      ),
      switchMap((anyMigrated) => {
        if (anyMigrated) {
          return persist$(project).pipe(
            defaultIfEmpty(null),
            map(() => project),
            catchError(() => of(project)),
          );
        }
        return of(project);
      }),
      switchMap((project) => this.projectFloorplanService.loadFloorplans(project).pipe(map(() => project))),
      switchMap((project) => {
        const p = project as unknown as Project;
        if (this.projectFloorplanItemReferenceMigrationService.needsMigration(p)) {
          this.projectFloorplanItemReferenceMigrationService.migrateFloorplanItemReferences(p);
          return persist$(project).pipe(
            defaultIfEmpty(null),
            map(() => project),
            catchError(() => of(project)),
          );
        }
        return of(project);
      }),
    );
  }
}
